import React, { Fragment } from 'react';
import T from 'prop-types';
import { isEmpty } from 'lodash-es';

import TruncatedItems from '../TruncatedItems';

// Options for build labels in the preview
import { getActiveOption } from '../../../DateRangePicker/configs/predefined-ranges';
import { dataUseOptions } from '../UpsertExportForm/ExportDataUse';
import { geoGranularities } from '../UpsertExportForm/GeoGranularity';
import { layouts as dataLayouts } from '../UpsertExportForm/DataLayout';
import { growthRatesGroupOptions } from '../UpsertExportForm/GrowthRates';
import { formatTypes as exportFormatTypes } from '../UpsertExportForm/ExportType';
import { granularities as rawDataGranularities } from '../UpsertExportForm/DateGranularity';
import {
  kindGranularities,
  storeGranularities,
  fromEntityGranularity,
} from '../UpsertExportForm/EntityGranularity';
import {
  baseDataPointsOptions,
  csvOnlyBaseDataPointsOptions,
  advancedDataPointsOptions,
} from '../../configs/report';
import { COHORTS } from '../../../../utils/advanced-metrics';
import { RENDER_FIELDS } from '../../configs/constants';
import { format } from '../../../../utils/date';

const ExportSettingsPreview = ({ report }) => {
  const active = getActiveOption(
    format(report.start_date),
    format(report.end_date),
    format(report.end_date)
  );

  return (
    <div className="export-settings-preview">
      <h3 className="export-settings-preview-title">Export Settings</h3>
      <div className="export-settings-preview-grid">
        <div className="text-bold">Date Range</div>
        {active.value === 'custom' ? (
          <div>
            {format(report.start_date)} – {format(report.end_date)}
          </div>
        ) : (
          <div>
            {active.label} ({format(report.start_date)} – {format(report.end_date)})
          </div>
        )}

        <div className="text-bold">Entities</div>
        <div>
          <TruncatedItems items={(report.entities || []).map((entity) => entity.value)} />
        </div>
        {RENDER_FIELDS[report.data_type].map((field) => {
          if (field === 'data_points') {
            return (
              <Fragment key={field}>
                <div className="text-bold">Datapoints</div>
                <div>
                  <TruncatedItems items={getDataPointLabels(report)} />
                </div>
              </Fragment>
            );
          }

          if (field === 'user_segments') {
            return (
              <Fragment key={field}>
                <div className="text-bold">Segments</div>
                <div>
                  <TruncatedItems
                    items={(report.user_segments || []).map(
                      (segment) => COHORTS[segment] || segment
                    )}
                  />
                </div>
              </Fragment>
            );
          }

          if (field === 'countries_iso') {
            return (
              <Fragment key={field}>
                <div className="text-bold">Regions</div>
                <div>
                  <TruncatedItems items={getRegionsLabels(report)} />
                </div>
              </Fragment>
            );
          }

          if (field === 'growth_rates') {
            return (
              <Fragment key={field}>
                <div className="text-bold">Growth Rates</div>
                <div>
                  {!isEmpty(report.growth_rates) ? (
                    <TruncatedItems items={getGrowthRatesLabels(report)} />
                  ) : (
                    '-'
                  )}
                </div>
              </Fragment>
            );
          }

          if (field === 'use_entity_relations') {
            return (
              <Fragment key={field}>
                <div className="text-bold">Export Data Use</div>
                <div>{getDataUseLabel(report)}</div>
              </Fragment>
            );
          }

          if (field === 'geo_granularity') {
            return (
              <Fragment key={field}>
                <div className="text-bold">Geo Granularity</div>
                <div>{getGeoGranularityLabel(report)}</div>
              </Fragment>
            );
          }

          if (field === 'data_granularity') {
            return (
              <Fragment key={field}>
                <div className="text-bold">Raw Data Granularity</div>
                <div className="text-capitalize">{getRawDataGranularityLabel(report)}</div>
              </Fragment>
            );
          }

          if (field === 'data_schema') {
            const { kindGranularity, storeGranularity } = getEntityGranularityLabels(report);
            return (
              <Fragment key={field}>
                <div className="text-bold">Entity Aggregation</div>
                <div>{kindGranularity}</div>
                <div className="text-bold">Store Granularity</div>
                <div>{storeGranularity}</div>
              </Fragment>
            );
          }

          if (field === 'output_format') {
            return (
              <Fragment key={field}>
                <div className="text-bold">Layout</div>
                <div>{getDataLayoutLabel(report)}</div>
              </Fragment>
            );
          }

          if (field === 'export_type') {
            return (
              <Fragment key={field}>
                <div className="text-bold">Export Type</div>
                <div className="text-uppercase">{getExportTypeLabel(report)}</div>
              </Fragment>
            );
          }

          return null;
        })}
        {report.recipients?.length > 0 && (
          <>
            <div className="text-bold">Recipients</div>
            <div>
              {report.recipients?.length ? report.recipients.map((r) => r.value).join(', ') : '-'}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const labelsFromEntries = (entries) =>
  entries.reduce(
    (acc, [value, label]) => ({
      ...acc,
      [value]: label,
    }),
    {}
  );

function getDataPointLabels(report) {
  if (report.data_type === 'mpi') return ['MPI'];

  const options =
    report.data_type === 'advanced'
      ? advancedDataPointsOptions(report.panel_version)
      : baseDataPointsOptions.concat(csvOnlyBaseDataPointsOptions);

  const labels = options.reduce(
    (acc, option) => ({
      ...acc,
      [option.value]: option.text,
    }),
    {}
  );

  return (report.data_points || []).map((value) => labels[value] || value);
}

function getRawDataGranularityLabel(report) {
  const labels = labelsFromEntries(rawDataGranularities);
  return labels[report.data_granularity] || report.data_granularity;
}

function getDataLayoutLabel(report) {
  const labels = labelsFromEntries(dataLayouts);
  return labels[report.output_format] || report.output_format;
}

function getExportTypeLabel(report) {
  const labels = labelsFromEntries(exportFormatTypes);
  return labels[report.export_type] || report.export_type;
}

function getGeoGranularityLabel(report) {
  const labels = labelsFromEntries(geoGranularities);
  return labels[report.geo_granularity] || report.geo_granularity;
}

function getDataUseLabel(report) {
  const dataUseEntries = dataUseOptions.map((option) => [option.value, option.text]);
  const labels = labelsFromEntries(dataUseEntries);
  return labels[report.use_entity_relations] || report.use_entity_relations;
}

function getEntityGranularityLabels(report) {
  const { kindGranularity, storeGranularity } = fromEntityGranularity[report.data_schema] || {};
  const kindLabels = labelsFromEntries(kindGranularities);
  const storeLabels = labelsFromEntries(storeGranularities);
  return {
    kindGranularity: kindLabels[kindGranularity] || kindGranularity,
    storeGranularity: storeLabels[storeGranularity] || storeGranularity,
  };
}

function getGrowthRatesLabels(report) {
  const {
    old: oldGrowthRates,
    periods: periodGrowthRates,
    granularities: granularitiesGrowthRates,
  } = growthRatesGroupOptions;
  const labels = oldGrowthRates
    .concat(periodGrowthRates)
    .concat(granularitiesGrowthRates)
    .reduce(
      (acc, option) => ({
        ...acc,
        [option.value]: option.text || option.label,
      }),
      {}
    );

  return (report.growth_rates || []).map((value) => {
    if (value.split(':').length === 2) {
      const [period, granularity] = value.split(':');
      return `${labels[period]} – ${labels[granularity]}`;
    }
    return labels[value] || value;
  });
}

function getRegionsLabels(report) {
  let selectedCountries = (report.countries_iso || []).concat(report.include_ww ? 'WW' : []);
  const selectedRegions = TINKERBELL_REGIONS.reduce((result, item) => {
    if (new Set([...item.list, ...selectedCountries]).size === selectedCountries.length) {
      // Exclude the countries of this region from the selectedCountries.
      selectedCountries = selectedCountries.filter((countryIso) => !item.list.includes(countryIso));
      return [...result, item.text];
    }

    return result;
  }, []);

  return selectedRegions.concat(selectedCountries);
}

ExportSettingsPreview.propTypes = {
  report: T.object.isRequired,
};

export default ExportSettingsPreview;
